import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IProductUnit } from '../../../types/productUnit/productUnitInterfaces';
import { RestaurantMenuItemDTO } from '../../../types/restaurant/RestaurantMenuTypes';
import { RestaurantRawMaterialDTO } from '../../../types/restaurant/RestaurantRawMaterialTypes';
import {
  RestaurantRecipeForm,
  RestaurantRecipeIngredientForm,
  RestaurantRecipePage,
} from '../../../types/restaurant/RestaurantRecipeTypes';
import { RestaurantUnitConversionDTO } from '../../../types/restaurant/RestaurantUnitConversionTypes';

const newIngredient = (key: number): RestaurantRecipeIngredientForm => ({
  key,
  rawMaterialId: '',
  quantity: 1,
  unitId: '',
  notes: '',
  orderIndex: 0,
});

export const emptyRecipeForm: RestaurantRecipeForm = {
  menuItemId: '',
  name: '',
  description: '',
  yieldQuantity: 1,
  yieldUnitId: '',
  inventoryMode: 'DIRECT_ORDER_CONSUMPTION',
  active: true,
  ingredients: [newIngredient(1)],
};

interface State {
  page: RestaurantRecipePage | null;
  menuItems: RestaurantMenuItemDTO[];
  rawMaterials: RestaurantRawMaterialDTO[];
  units: IProductUnit[];
  conversions: RestaurantUnitConversionDTO[];
  form: RestaurantRecipeForm;
  loading: boolean;
  loadingOptions: boolean;
  saving: boolean;
  modalOpen: boolean;
  nextIngredientKey: number;
}

const initialState: State = {
  page: null,
  menuItems: [],
  rawMaterials: [],
  units: [],
  conversions: [],
  form: emptyRecipeForm,
  loading: false,
  loadingOptions: false,
  saving: false,
  modalOpen: false,
  nextIngredientKey: 2,
};

const slice = createSlice({
  name: 'restaurantRecipe',
  initialState,
  reducers: {
    setRecipePage: (state, action: PayloadAction<RestaurantRecipePage>) => {
      state.page = action.payload;
    },
    setRecipeOptions: (state, action: PayloadAction<Pick<State, 'menuItems' | 'rawMaterials' | 'units' | 'conversions'>>) => {
      state.menuItems = action.payload.menuItems;
      state.rawMaterials = action.payload.rawMaterials;
      state.units = action.payload.units;
      state.conversions = action.payload.conversions;
    },
    setRecipeMenuItems: (state, action: PayloadAction<RestaurantMenuItemDTO[]>) => {
      state.menuItems = action.payload;
    },
    appendRecipeMenuItem: (state, action: PayloadAction<RestaurantMenuItemDTO>) => {
      if (!state.menuItems.some((item) => item.id === action.payload.id)) {
        state.menuItems = [...state.menuItems, action.payload];
      }
    },
    setRecipeLoading: (state, action: PayloadAction<boolean>) => { state.loading = action.payload; },
    setRecipeOptionsLoading: (state, action: PayloadAction<boolean>) => { state.loadingOptions = action.payload; },
    setRecipeSaving: (state, action: PayloadAction<boolean>) => { state.saving = action.payload; },
    openRecipeCreate: (state) => {
      state.form = { ...emptyRecipeForm, ingredients: [newIngredient(1)] };
      state.nextIngredientKey = 2;
      state.modalOpen = true;
    },
    openRecipeEdit: (state, action: PayloadAction<RestaurantRecipeForm>) => {
      state.form = action.payload;
      state.nextIngredientKey = Math.max(1, ...action.payload.ingredients.map((item) => item.key)) + 1;
      state.modalOpen = true;
    },
    closeRecipeModal: (state) => {
      state.form = { ...emptyRecipeForm, ingredients: [newIngredient(1)] };
      state.modalOpen = false;
    },
    updateRecipeField: (state, action: PayloadAction<{ field: keyof Omit<RestaurantRecipeForm, 'ingredients'>; value: string | number | boolean }>) => {
      state.form = { ...state.form, [action.payload.field]: action.payload.value };
    },
    addRecipeIngredient: (state) => {
      state.form.ingredients.push(newIngredient(state.nextIngredientKey++));
    },
    removeRecipeIngredient: (state, action: PayloadAction<number>) => {
      state.form.ingredients = state.form.ingredients.filter((item) => item.key !== action.payload);
    },
    updateRecipeIngredient: (state, action: PayloadAction<{ key: number; field: keyof Omit<RestaurantRecipeIngredientForm, 'key'>; value: string | number }>) => {
      const ingredient = state.form.ingredients.find((item) => item.key === action.payload.key);
      if (ingredient) ingredient[action.payload.field] = action.payload.value as never;
    },
    moveRecipeIngredient: (state, action: PayloadAction<{ key: number; direction: -1 | 1 }>) => {
      const index = state.form.ingredients.findIndex((item) => item.key === action.payload.key);
      const target = index + action.payload.direction;
      if (index >= 0 && target >= 0 && target < state.form.ingredients.length) {
        [state.form.ingredients[index], state.form.ingredients[target]] = [state.form.ingredients[target], state.form.ingredients[index]];
      }
    },
  },
});

export const {
  setRecipePage, setRecipeOptions, setRecipeMenuItems, appendRecipeMenuItem,
  setRecipeLoading, setRecipeOptionsLoading,
  setRecipeSaving, openRecipeCreate, openRecipeEdit, closeRecipeModal,
  updateRecipeField, addRecipeIngredient, removeRecipeIngredient,
  updateRecipeIngredient, moveRecipeIngredient,
} = slice.actions;

export default slice.reducer;
